import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CalendarRange } from 'lucide-react';

const KIND_BADGE = {
  update: 'bg-forest/10 text-forest',
  decision: 'bg-blue-50 text-blue-700',
  support_request: 'bg-terracotta/10 text-terracotta'
};

const QUARTER_MONTHS = ['Aug – Oct', 'Nov – Jan', 'Feb – Apr', 'May – Jul'];
const programQuarter = (d) => Math.floor(((d.getMonth() - 7 + 12) % 12) / 3);
const programStartYear = (d) => (d.getMonth() >= 7 ? d.getFullYear() : d.getFullYear() - 1);

export const LegacyTimeline = ({ project, entries }) => {
  const now = new Date();
  const startYear = programStartYear(now);
  const yearLabel = `${startYear}–${String(startYear + 1).slice(2)}`;
  const currentQ = programQuarter(now);
  const quarters = ['q1', 'q2', 'q3', 'q4'].map((q, i) => ({
    key: q,
    label: `Q${i + 1}`,
    months: QUARTER_MONTHS[i],
    milestone: project?.milestones?.[q] || '',
    entries: (entries || []).filter(e => {
      const d = new Date(e.created_at);
      return programStartYear(d) === startYear && programQuarter(d) === i;
    })
  }));

  const hasContent = quarters.some(q => q.milestone || q.entries.length > 0);
  if (!hasContent) return null;

  return (
    <Card className="card-base" data-testid="legacy-timeline">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <CalendarRange className="h-5 w-5 text-terracotta" />
          <p className="font-heading font-semibold text-forest">The Year in Motion · {yearLabel}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {quarters.map((q, i) => (
            <div key={q.key}
              className={`rounded-xl border p-3 ${i === currentQ ? 'border-terracotta/50 bg-terracotta/5' : i < currentQ ? 'border-sand bg-sand/30' : 'border-dashed border-sand'}`}
              data-testid={`timeline-quarter-${q.key}`}>
              <div className="flex items-center justify-between">
                <p className="font-heading font-semibold text-forest">{q.label}</p>
                <span className="text-xs text-gray-400">{q.months}</span>
              </div>
              {i === currentQ && <Badge className="bg-terracotta/10 text-terracotta text-xs mt-1">Now</Badge>}
              {q.milestone ? (
                <p className="text-sm text-gray-700 mt-2">{q.milestone}</p>
              ) : (
                <p className="text-xs text-gray-400 italic mt-2">No milestone set</p>
              )}
              {q.entries.length > 0 && (
                <div className="mt-3 space-y-1.5 border-t border-sand pt-2">
                  {q.entries.slice(0, 4).map(e => (
                    <div key={e.id} className="flex items-start gap-1.5">
                      <span className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] flex-shrink-0 ${KIND_BADGE[e.kind] || KIND_BADGE.update}`}>
                        {new Date(e.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                      </span>
                      <p className="text-xs text-gray-600 line-clamp-2">{e.content}</p>
                    </div>
                  ))}
                  {q.entries.length > 4 && <p className="text-[10px] text-gray-400">+{q.entries.length - 4} more in the log</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default LegacyTimeline;
